// src/pages/OnboardingWizard.tsx — Slice D commit 7.
//
// Three-step first-login wizard at /onboarding. Auto-triggered for users
// whose hub_users.onboarded_at is null (App.tsx guard); also reachable
// directly via the MePage "Complete your profile" nudge for users who
// pressed Skip earlier.
//
// Audit-row divergences from docs/prototype/onboarding.jsx baked in:
//   - Step 1 omits the prototype's Job title field — no hub_users.title
//     column (STATUS §3, audit row 9). Phone + manager (SSO only) + member id.
//   - Manager list comes from GET /api/contacts filtered on c.isManager
//     (camelCase API field, not the snake_case in the kickoff text);
//     slice(0,6) matches the prototype's top-6 pattern.
//   - Pre-populate when retriggered after Skip: fetch /api/personal for
//     phone + emergency, read me.line_manager_contact_id for manager.
//   - Unicode characters used directly: — → ← ✚. No \u escapes.
//
// Phase 9 follow-up (2026-05-21) — PIN volunteers no longer see the line-
// manager section in Step 1. The PUT to /api/personal omits line_manager_name
// for PIN sessions (existing data on hub_users is preserved). The PATCH to
// /api/me/line-manager remains guarded by `data.managerId !== null` which
// stays null for PIN users since the picker is hidden.
//
// Save flow (sequential — each await must succeed before the next fires;
// any failure surfaces inline and lets the user retry):
//   1. PUT /api/personal (phone + emergency, plus line_manager_name only on SSO)
//   2. PATCH /api/me/line-manager (only when a manager was picked — SSO-only path)
//   3. POST /api/me/onboarded { skipped: false }
//   4. navigate("/")
//
// Skip flow: POST /api/me/onboarded { skipped: true } → navigate("/").

import { useEffect, useMemo, useState } from "react";
import { ApiError, apiFetch } from "../api";
import { useCurrentUser } from "../auth";
import { C, F } from "../theme";
import {
  Avatar,
  Button,
  Card,
  Eyebrow,
  Field,
  Icons,
  Pill,
} from "../components/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ContactRow {
  id: number;
  first: string;
  last: string;
  isManager: boolean;
}

interface ContactsListResponse {
  contacts: ContactRow[];
}

interface PersonalResponse {
  mobile_number: string | null;
  emergency_name: string | null;
  emergency_relationship: string | null;
  emergency_phone: string | null;
  emergency_alt_phone: string | null;
}

interface SuggestionsResponse {
  suggestions: string[];
}

interface WizardData {
  phone: string;
  em_name: string;
  em_rel: string;
  em_phone: string;
  em_alt: string;
  managerId: number | null;
  managerName: string;
  /** NYMR membership number — Slice G. Loose 3–7 digit format; empty allowed. */
  memberId: string;
}

const BLANK: WizardData = {
  phone: "",
  em_name: "",
  em_rel: "",
  em_phone: "",
  em_alt: "",
  managerId: null,
  managerName: "",
  memberId: "",
};

/** Loose 3–7 digit format (Chris's "I entered 4 numbers" complaint). */
const MEMBER_ID_REGEX = /^\d{3,7}$/;

// ---------------------------------------------------------------------------
// Wizard
// ---------------------------------------------------------------------------

export function OnboardingWizard() {
  const { me } = useCurrentUser();

  const [step, setStep] = useState<1 | 2>(1);
  const [data, setData] = useState<WizardData>(BLANK);
  const [managers, setManagers] = useState<ContactRow[] | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [memberIdError, setMemberIdError] = useState<string | null>(null);

  const isRetrigger = me?.onboarded_at !== null && me?.onboarded_skipped === true;
  // Phase 9 follow-up (2026-05-21) — PIN volunteers don't have a line manager
  // surface anywhere in the app. Skip the Step 1 picker / suggestions / free
  // text entirely for them and omit line_manager_name from the PUT body.
  const showLineManager = me?.authMethod !== "pin";

  // Fetch managers in parallel with optional pre-population. Skip the manager
  // fetches for PIN users entirely — they don't need them.
  useEffect(() => {
    if (!me) return;
    let cancelled = false;

    const promises: Array<Promise<unknown>> = [];

    if (showLineManager) {
      promises.push(
        apiFetch<ContactsListResponse>("/api/contacts").then((res) => {
          if (cancelled) return;
          setManagers(res.contacts.filter((c) => c.isManager));
        }),
        apiFetch<SuggestionsResponse>("/api/me/manager-suggestions")
          .then((res) => {
            if (cancelled) return;
            // Case-insensitive client-side dedup on top of the server's DISTINCT.
            const seen = new Set<string>();
            const unique = res.suggestions.filter((s) => {
              const k = s.trim().toLowerCase();
              if (seen.has(k)) return false;
              seen.add(k);
              return true;
            });
            setSuggestions(unique);
          })
          .catch(() => {
            // Best-effort — the wizard works without suggestions.
            if (!cancelled) setSuggestions([]);
          }),
      );
    } else {
      // PIN — no managers needed; flag the list as "loaded empty" so Step1
      // doesn't render its loading spinner.
      setManagers([]);
    }

    if (isRetrigger) {
      promises.push(
        apiFetch<PersonalResponse>("/api/personal").then((res) => {
          if (cancelled) return;
          setData({
            phone: res.mobile_number ?? "",
            em_name: res.emergency_name ?? "",
            em_rel: res.emergency_relationship ?? "",
            em_phone: res.emergency_phone ?? "",
            em_alt: res.emergency_alt_phone ?? "",
            // For PIN users, the line-manager fields are hidden in Step1 and
            // line_manager_name is omitted from the PUT — but the wizard still
            // mirrors the current me values so a switched-auth user (SSO→PIN)
            // doesn't accidentally see them flicker as empty in Step3 review.
            managerId: showLineManager ? me.line_manager_contact_id : null,
            managerName: showLineManager ? (me.line_manager_name ?? "") : "",
            memberId: me.member_id_number !== null ? String(me.member_id_number) : "",
          });
        }),
      );
    } else {
      // Initial onboarding — pre-fill the member-id from /api/me too. The
      // user might have linked their membership via the /me/member-id self-
      // claim page before completing onboarding.
      if (me.member_id_number !== null) {
        setData((prev) => ({ ...prev, memberId: String(me.member_id_number) }));
      }
    }

    Promise.allSettled(promises).then(() => {
      if (!cancelled) setHydrated(true);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.hubUserId]);

  const update = <K extends keyof WizardData>(k: K, v: WizardData[K]) => {
    setData((prev) => ({ ...prev, [k]: v }));
  };

  // -------------------------------------------------------------------------
  // Save + skip
  // -------------------------------------------------------------------------

  async function saveAndContinue() {
    setSubmitting(true);
    setError(null);
    setMemberIdError(null);

    // Validate member-id up-front. Format error blocks the whole submit.
    const memberIdTrimmed = data.memberId.trim();
    if (memberIdTrimmed !== "" && !MEMBER_ID_REGEX.test(memberIdTrimmed)) {
      setMemberIdError(
        "Membership number must be 3–7 digits. Leave blank if you don't have one.",
      );
      setStep(1);
      setSubmitting(false);
      return;
    }

    try {
      // Step 1: personal details (phone + emergency only — address fields
      // omitted so the upsert leaves them untouched). Slice F added
      // line_manager_name on the same PUT (server routes it to hub_users);
      // Phase 9 follow-up (2026-05-21) omits it for PIN sessions so PIN
      // users' existing hub_users.line_manager_name is left untouched.
      // Emergency contact is NOT collected during onboarding (2026-07-24) — it's
      // managed by HR on the People profile. Omitted fields are left untouched
      // by the upsert.
      const personalBody: Record<string, unknown> = {
        mobile_number: data.phone || null,
      };
      if (showLineManager) {
        personalBody.line_manager_name = data.managerName;
      }
      await apiFetch("/api/personal", {
        method: "PUT",
        body: JSON.stringify(personalBody),
      });

      // Step 2: line manager picker (only when picked AND SSO — the picker
      // is hidden for PIN so data.managerId stays null, but belt + braces).
      if (showLineManager && data.managerId !== null) {
        await apiFetch("/api/me/line-manager", {
          method: "PATCH",
          body: JSON.stringify({ contactId: data.managerId }),
        });
      }

      // Step 3: stamp onboarded_at.
      await apiFetch("/api/me/onboarded", {
        method: "POST",
        body: JSON.stringify({ skipped: false }),
      });

      // Slice G — member-id is best-effort: a failure here is surfaced inline
      // on Step 3 but does NOT block navigate-home. Onboarding is "complete"
      // once onboarded_at is stamped regardless.
      if (memberIdTrimmed !== "") {
        try {
          await apiFetch("/api/me/member-id", {
            method: "PATCH",
            body: JSON.stringify({ memberIdNumber: Number(memberIdTrimmed) }),
          });
        } catch (e) {
          setMemberIdError(
            e instanceof ApiError
              ? `Couldn't save membership number: ${e.message}`
              : "Couldn't save membership number.",
          );
        }
      }

      // Hard-reload to "/" so RequireAuth re-mounts and reads the fresh
      // onboarded_at from /api/me. The previous attempt — await refresh()
      // then navigate() — failed because each useCurrentUser invocation
      // owns its own state; refresh() updates only the wizard's local
      // instance, while RequireAuth's gate keeps its stale me (with
      // onboarded_at=null) and bounces us straight back to /onboarding.
      window.location.replace("/");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Save failed — please try again");
      setSubmitting(false);
    }
  }

  async function skip() {
    setSubmitting(true);
    setError(null);
    try {
      await apiFetch("/api/me/onboarded", {
        method: "POST",
        body: JSON.stringify({ skipped: true }),
      });
      // Hard-reload to "/" — see saveAndContinue() for the rationale.
      window.location.replace("/");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Skip failed — please try again");
      setSubmitting(false);
    }
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (!me) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: C.paper,
          color: C.inkSoft,
          fontFamily: F.body,
          fontWeight: 600,
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: `radial-gradient(circle at 20% 10%, ${C.brassSurf}, ${C.paper} 60%, ${C.paperWarm} 100%)`,
        padding: "24px 18px 32px",
        position: "relative",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <Eyebrow
          style={{
            fontSize: 11,
            letterSpacing: 3,
            textTransform: "uppercase",
            fontWeight: 800,
          }}
        >
          NYMR Info Hub
        </Eyebrow>
        <button
          type="button"
          onClick={skip}
          disabled={submitting}
          style={{
            background: "none",
            border: "none",
            color: C.inkSoft,
            fontFamily: F.serif,
            fontStyle: "italic",
            fontSize: 13,
            fontWeight: 600,
            cursor: submitting ? "default" : "pointer",
            opacity: submitting ? 0.5 : 1,
          }}
        >
          Skip for now
        </button>
      </div>

      <div style={{ textAlign: "center", marginBottom: 8 }}>
        <span style={{ display: "inline-block" }}>
          <Avatar first={me.first} last={me.last} size={56} tone="brass" />
        </span>
      </div>

      <h1
        style={{
          fontFamily: F.serif,
          fontSize: 24,
          fontWeight: 700,
          color: C.ink,
          textAlign: "center",
          margin: "10px 0 4px",
          letterSpacing: -0.4,
        }}
      >
        Welcome, {me.first}.
      </h1>
      <p
        style={{
          fontFamily: F.serif,
          fontStyle: "italic",
          fontSize: 14,
          color: C.brassDk,
          textAlign: "center",
          fontWeight: 600,
          marginBottom: 20,
        }}
      >
        Let's set up your profile.
      </p>

      <ProgressDots step={step} />

      <Card style={{ maxWidth: 380, margin: "0 auto" }}>
        <StepHeading step={step} />

        {step === 1 && (
          <Step1
            data={data}
            update={update}
            managers={managers}
            suggestions={suggestions}
            hydrated={hydrated || !isRetrigger}
            memberIdError={memberIdError}
            showLineManager={showLineManager}
            onContinue={() => setStep(2)}
          />
        )}

        {step === 2 && (
          <Step3
            data={data}
            managers={managers ?? []}
            submitting={submitting}
            error={error}
            memberIdError={memberIdError}
            onBack={() => setStep(1)}
            onSave={saveAndContinue}
            onSkip={skip}
          />
        )}
      </Card>

      <div
        style={{
          textAlign: "center",
          marginTop: 20,
          fontFamily: F.serif,
          fontStyle: "italic",
          fontSize: 12,
          color: C.inkMute,
        }}
      >
        Any of this can be changed later under <strong>Me → My details</strong>.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ProgressDots({ step }: { step: 1 | 2 }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        justifyContent: "center",
        margin: "0 0 24px",
      }}
      aria-hidden="true"
    >
      {([1, 2] as const).map((i) => (
        <div
          key={i}
          style={{
            width: step === i ? 24 : 8,
            height: 8,
            borderRadius: 8,
            background: step >= i ? C.brassDk : C.paperWarm,
            border: step >= i ? "none" : `1px solid ${C.hairline}`,
            transition: "all 0.2s",
          }}
        />
      ))}
    </div>
  );
}

function StepHeading({ step }: { step: 1 | 2 }) {
  const title = step === 1 ? "Tell us about you" : "All set";
  const sub =
    step === 1
      ? "We'll use these so colleagues can reach you."
      : "Review and confirm. You can change anything later.";
  return (
    <div style={{ textAlign: "center", marginBottom: 18 }}>
      <Eyebrow
        style={{
          fontSize: 11,
          letterSpacing: 2.5,
          fontWeight: 800,
          textTransform: "uppercase",
        }}
      >
        Step {step} of 2
      </Eyebrow>
      <h2
        style={{
          fontFamily: F.serif,
          fontSize: 26,
          fontWeight: 700,
          color: C.ink,
          margin: "6px 0 4px",
          letterSpacing: -0.4,
        }}
      >
        {title}
      </h2>
      <p
        style={{
          fontFamily: F.serif,
          fontStyle: "italic",
          fontSize: 14,
          color: C.inkSoft,
          fontWeight: 600,
          margin: 0,
          lineHeight: 1.45,
        }}
      >
        {sub}
      </p>
    </div>
  );
}

function Step1({
  data,
  update,
  managers,
  suggestions,
  hydrated,
  memberIdError,
  showLineManager,
  onContinue,
}: {
  data: WizardData;
  update: <K extends keyof WizardData>(k: K, v: WizardData[K]) => void;
  managers: ContactRow[] | null;
  suggestions: string[];
  hydrated: boolean;
  memberIdError: string | null;
  showLineManager: boolean;
  onContinue: () => void;
}) {
  const topManagers = useMemo(() => (managers ?? []).slice(0, 6), [managers]);

  // Reveal the free-text Field when the user pre-populated it (e.g. retrigger
  // after Skip), when they tap "Manager not listed?", or when they tap a
  // suggestion pill (which also pre-fills the field).
  const [freeTextOpen, setFreeTextOpen] = useState(data.managerName.trim() !== "");
  useEffect(() => {
    if (data.managerName.trim() !== "") setFreeTextOpen(true);
  }, [data.managerName]);

  const memberIdLocalError =
    data.memberId.trim() !== "" && !MEMBER_ID_REGEX.test(data.memberId.trim())
      ? "Membership number must be 3–7 digits."
      : null;

  return (
    <>
      <Field
        label="Phone number"
        value={data.phone}
        onChange={(v) => update("phone", v)}
        type="tel"
        ph="07700 900000"
      />
      <div
        style={{
          fontFamily: F.serif,
          fontStyle: "italic",
          fontSize: 12,
          color: C.inkSoft,
          margin: "-8px 0 14px",
          paddingLeft: 2,
          lineHeight: 1.4,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <Icons.Pin s={12} c={C.brassDk} />
        Only visible to you and your line manager.
      </div>

      {showLineManager && (
        <>
          <Eyebrow style={{ marginBottom: 8 }}>Line manager <span style={{ color: C.inkMute, fontStyle: "italic", fontWeight: 600 }}>(optional)</span></Eyebrow>
          {!hydrated || managers === null ? (
            <div
              style={{
                fontFamily: F.serif,
                fontStyle: "italic",
                color: C.inkMute,
                padding: "10px 0",
              }}
            >
              Loading managers...
            </div>
          ) : topManagers.length === 0 ? (
            <div
              style={{
                fontFamily: F.serif,
                fontStyle: "italic",
                color: C.inkMute,
                padding: "10px 0",
              }}
            >
              No managers in the directory yet.
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                marginBottom: 8,
              }}
            >
              {topManagers.map((m) => {
                const active = data.managerId === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => update("managerId", active ? null : m.id)}
                    style={{
                      padding: "9px 12px",
                      borderRadius: 10,
                      background: active ? C.brassBg : C.card,
                      color: active ? C.brassDk : C.ink,
                      border: active
                        ? `1.5px solid ${C.brassDk}`
                        : `1px solid ${C.hairline}`,
                      fontFamily: F.body,
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {m.first} {m.last}
                  </button>
                );
              })}
            </div>
          )}

          {/* Suggestion pills + "Manager not listed?" toggle — Slice G */}
          {suggestions.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <Eyebrow style={{ marginBottom: 6 }}>Or pick from suggestions</Eyebrow>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {suggestions.map((s) => {
                  const active =
                    data.managerName.trim().toLowerCase() === s.trim().toLowerCase();
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => {
                        update("managerName", s);
                        update("managerId", null);
                        setFreeTextOpen(true);
                      }}
                      style={{
                        padding: "7px 11px",
                        borderRadius: 999,
                        border: active
                          ? `1.5px solid ${C.brassDk}`
                          : `1px solid ${C.hairline}`,
                        background: active ? C.brassBg : C.paperWarm,
                        color: active ? C.brassDk : C.inkSoft,
                        fontFamily: F.body,
                        fontSize: 12.5,
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ marginTop: 10 }}>
            {!freeTextOpen ? (
              <button
                type="button"
                onClick={() => setFreeTextOpen(true)}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
                <Pill tone="heather" small>
                  Manager not listed?
                </Pill>
              </button>
            ) : (
              <Field
                label="Manager name"
                value={data.managerName}
                onChange={(v) => update("managerName", v)}
                ph="Type your line manager's name"
                max={200}
              />
            )}
          </div>
          <div
            style={{
              fontFamily: F.serif,
              fontStyle: "italic",
              fontSize: 12,
              color: C.inkMute,
              fontWeight: 600,
              lineHeight: 1.45,
              padding: "0 2px 4px",
            }}
          >
            Pick from the list, type a name, or leave both blank — you can edit this later under{" "}
            <strong>Me → My details</strong>.
          </div>
        </>
      )}

      {/* ID Card Number — Phase 11 / Q1 relabel of "NYMR membership number".
          Field value still maps to `member_id_number` server-side. */}
      <div style={{ marginTop: 16 }}>
        <Field
          label="ID Card Number"
          value={data.memberId}
          onChange={(v) => update("memberId", v)}
          type="number"
          ph="e.g. 27980"
        />
        <div
          style={{
            fontFamily: F.serif,
            fontStyle: "italic",
            fontSize: 12,
            color: memberIdLocalError || memberIdError ? C.signalDk : C.inkMute,
            fontWeight: 600,
            lineHeight: 1.45,
            padding: "0 2px 4px",
            margin: "-6px 0 0",
          }}
        >
          {memberIdLocalError ?? memberIdError ?? (
            <>
              Your NYMR ID Card Number — typically 4–6 digits, e.g. 27980. Leave blank
              if you don&apos;t have one yet.
            </>
          )}
        </div>
      </div>

      <Button
        variant="primary"
        fullWidth
        onClick={onContinue}
        disabled={memberIdLocalError !== null}
        style={{ marginTop: 16 }}
      >
        Continue →
      </Button>
    </>
  );
}

function Step3({
  data,
  managers,
  submitting,
  error,
  memberIdError,
  onBack,
  onSave,
  onSkip,
}: {
  data: WizardData;
  managers: ContactRow[];
  submitting: boolean;
  error: string | null;
  memberIdError: string | null;
  onBack: () => void;
  onSave: () => void;
  onSkip: () => void;
}) {
  const managerName = useMemo(() => {
    if (data.managerId !== null) {
      const m = managers.find((x) => x.id === data.managerId);
      return m ? `${m.first} ${m.last}` : "—";
    }
    return data.managerName.trim() || null;
  }, [data.managerId, data.managerName, managers]);

  const empty =
    !data.phone &&
    !data.managerId &&
    !data.managerName.trim() &&
    !data.em_name &&
    !data.memberId.trim();

  return (
    <>
      <div style={{ padding: "4px 0 16px" }}>
        {data.phone && (
          <ReviewRow label="Phone" mono>
            {data.phone}
          </ReviewRow>
        )}
        {managerName && (
          <ReviewRow label="Line manager">{managerName}</ReviewRow>
        )}
        {data.memberId.trim() && (
          <ReviewRow label="ID Card Number" mono>
            #{data.memberId.trim()}
          </ReviewRow>
        )}
        {empty && (
          <div
            style={{
              padding: "18px 0",
              textAlign: "center",
              fontFamily: F.serif,
              fontStyle: "italic",
              color: C.inkMute,
            }}
          >
            You haven't filled anything in yet — that's fine.
          </div>
        )}
      </div>

      {error && (
        <div
          style={{
            padding: 12,
            marginBottom: 12,
            borderRadius: 12,
            background: C.signalBg,
            border: `1px solid rgba(184,58,43,0.22)`,
            color: C.signalDk,
            fontFamily: F.body,
            fontWeight: 700,
            fontSize: 13,
          }}
          role="status"
        >
          {error}
        </div>
      )}
      {!error && memberIdError && (
        <div
          style={{
            padding: 12,
            marginBottom: 12,
            borderRadius: 12,
            background: C.signalBg,
            border: `1px solid rgba(184,58,43,0.22)`,
            color: C.signalDk,
            fontFamily: F.body,
            fontWeight: 700,
            fontSize: 13,
          }}
          role="status"
        >
          {memberIdError}
        </div>
      )}

      <div style={{ display: "flex", gap: 10 }}>
        <Button variant="secondary" fullWidth disabled={submitting} onClick={onBack}>
          ← Back
        </Button>
        <Button variant="primary" fullWidth disabled={submitting} onClick={onSave}>
          {submitting ? "Saving..." : "Save & continue"}
        </Button>
      </div>
      <Button
        variant="ghost"
        fullWidth
        disabled={submitting}
        onClick={onSkip}
        style={{
          marginTop: 8,
          fontFamily: F.serif,
          fontStyle: "italic",
          fontWeight: 600,
          fontSize: 13,
        }}
      >
        I'll do this later
      </Button>
    </>
  );
}

function ReviewRow({
  label,
  children,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div style={{ padding: "10px 0", borderTop: `1px solid ${C.hairlineLt}` }}>
      <div
        style={{
          fontFamily: F.serif,
          fontStyle: "italic",
          fontSize: 12,
          color: C.brassDk,
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: mono ? F.mono : F.body,
          fontSize: 14,
          color: C.ink,
          fontWeight: 700,
          marginTop: 2,
        }}
      >
        {children}
      </div>
    </div>
  );
}
